import { ChartType } from "@components/charts";
import {
    DashboardTileType,
    IChartTileInfo,
    ICustomTileInfo,
    IGetTileDataArgs,
    ILinkTileInfo,
    ITableTileData,
    ITableTileInfo
} from "@components/dashboard";
import { getPluralizationLabel, IGetValueArgs } from "@components/smart/FieldInfo";
import { IColumn, IRow } from "@components/table";
import { IChartTileData } from "@components/tiles/chartTile/ChartTile";
import { CustomerPermissionCodes, UserPermissionCodes } from "@odata/CompanyPermissionEnums";
import BenefitLimitsProgress from "@pages/home/BenefitLimitsProgressTile";
import { currencyScaleFormatter } from "@pages/home/CustomerDashboardDef";
import EmployeeLeaveBalanceTile from "@pages/home/EmployeeLeaveBalanceTile";
import { createDateFromPeriod } from "@pages/payroll/payroll.utils";
import { getCompanyCurrency } from "@utils/CompanyUtils";
import customFetch from "@utils/customFetch";
import { isPayrollEnabledOnCompany } from "@utils/definition.utils";
import { roundToDecimalPlaces } from "@utils/general";
import { logger } from "@utils/log";
import memoize from "@utils/memoize";
import { memoizeOne } from "@utils/memoizeOne";
import { capitalize } from "@utils/string";
import i18next from "i18next";

import { REST_API_URL } from "../../constants";
import { IAppContext } from "../../contexts/appContext/AppContext.types";
import { TextAlign } from "../../enums";
import { ROUTE_EMPLOYEE_PAYSLIP, ROUTE_PAYSLIP_OVERVIEW } from "../../routes";
import CurrencyType from "../../types/Currency";
import DateType, { DateFormat } from "../../types/Date";

const EMPLOYEE_COMPENSATION_URL = `${REST_API_URL}/DashboardData/EmployeeCompensation`;
const EMPLOYEE_PAYROLL_URL = `${REST_API_URL}/DashboardData/EmployeePayroll`;
const EMPLOYEE_MONTHLY_BENEFITS_URL = `${REST_API_URL}/DashboardData/EmployeeMonthlyBenefits`;
const EMPLOYEE_LEAVE_BALANCE_URL = `${REST_API_URL}/DashboardData/EmployeeLeaveBalance`;

export const isPayrollEnabledOnCompanyCallback = (args: IGetValueArgs): boolean =>
    isPayrollEnabledOnCompany(args.context.getCompany());

export const getEmployeeLeaveBalance = memoizeOne(async (context: IAppContext): Promise<ILeaveBalanceData[]> => {
    if (!isPayrollEnabledOnCompany(context.getCompany())) {
        return [];
    }
    const res = await customFetch(EMPLOYEE_LEAVE_BALANCE_URL);
    return (await res.json()) as ILeaveBalanceData[];
}, (context) => [context.getCompany()?.Id]);

export function getEmployeePayslipTileDef(): ILinkTileInfo {
    return {
        type: DashboardTileType.Link,
        size: { w: 1, h: 1 },
        isVisible: isPayrollEnabledOnCompanyCallback,
        title: i18next.t("Home:EmployeePayslip.Heading"),
        link: ROUTE_EMPLOYEE_PAYSLIP,
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
        iconName: "Payslip",
    };
}

export function getPayslipOverviewTileDef(): ILinkTileInfo {
    return {
        type: DashboardTileType.Link,
        size: { w: 1, h: 1 },
        isVisible: isPayrollEnabledOnCompanyCallback,
        title: i18next.t("Home:PayslipOverview.Heading"),
        link: ROUTE_PAYSLIP_OVERVIEW,
        CPs: [UserPermissionCodes.PrPayroll, CustomerPermissionCodes.PrPayrollInfoConfidential],
        iconName: "PayslipOverview",
    };
}

interface ICompensationData {
    CalendarMonth: {
        Year: number;
        Month: number;
    };
    GrossSalary: number;
}

export async function getEmployeeCompensationChartTileData(_: IGetTileDataArgs): Promise<IChartTileData<ChartType.Line>> {
    let labels: string[] = [];
    let salaries: number[] = [];

    try {
        const res = await customFetch(EMPLOYEE_COMPENSATION_URL);
        const data = await res.json() as ICompensationData[];

        labels = data.sort((a, b) => {
            const dateA = createDateFromPeriod(a.CalendarMonth);
            const dateB = createDateFromPeriod(b.CalendarMonth);
            return dateA.getTime() - dateB.getTime();
        }).map(item => {
            const period = createDateFromPeriod(item.CalendarMonth);
            return DateType.format(period, "M/YY");
        });

        salaries = data.map(item => item.GrossSalary);
    } catch (e) {
        logger.error(e);
    }

    return {
        type: ChartType.Line,
        chartProps: {
            labels,
            values: [salaries],
            colors: ["C_CHART_green"],
        }
    };
}

export function getEmployeeCompensationTileDef(): IChartTileInfo {
    return {
        type: DashboardTileType.Chart,
        size: { w: 4, h: 2 },
        title: i18next.t("Home:EmployeeCompensation.Heading"),
        chartData: getEmployeeCompensationChartTileData,
        isVisible: isPayrollEnabledOnCompanyCallback,
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
        formatter: currencyScaleFormatter
    };
}

interface IEmployeePayrollData {
    CalendarMonth: {
        Year: number;
        Month: number;
    };
    GrossSalary: number;
    NetSalary: number;
}

const getEmployeePayrollData = async (args: IGetTileDataArgs): Promise<ITableTileData> => {
    const columns: IColumn[] = [
        { id: "period", label: i18next.t("Home:EmployeePayroll.Period"), textAlign: TextAlign.Left },
        { id: "grossSalary", label: i18next.t("Home:EmployeePayroll.GrossSalary"), textAlign: TextAlign.Right },
        { id: "netSalary", label: i18next.t("Home:EmployeePayroll.NetSalary"), textAlign: TextAlign.Right }
    ];

    const rows: IRow[] = [];

    try {
        const res = await customFetch(EMPLOYEE_PAYROLL_URL);
        const data = await res.json() as IEmployeePayrollData[];
        for (const item of data) {
            const period = createDateFromPeriod(item.CalendarMonth);
            rows.push({
                id: item.CalendarMonth?.Month,
                values: {
                    period: capitalize(DateType.format(period, DateFormat.monthAndYear)),
                    grossSalary: CurrencyType.format(item.GrossSalary, {
                        currency: getCompanyCurrency(args.context),
                        minimumFractionDigits: 0
                    }),
                    netSalary: CurrencyType.format(item.NetSalary, {
                        currency: getCompanyCurrency(args.context),
                        minimumFractionDigits: 0
                    }),
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
};

export function employeePayrollTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:EmployeePayroll.Heading"),
        size: { w: 3, h: 2 },
        isVisible: isPayrollEnabledOnCompanyCallback,
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
        tableData: getEmployeePayrollData,
    };
}

interface IEmployeeMonthlyBenefitsData {
    Name: string;
    EmploymentName: string;
    Value: number;
}

const getEmployeeMonthlyBenefitsData = async (args: IGetTileDataArgs): Promise<ITableTileData> => {
    let columns: IColumn[] = [
        { id: "name", label: i18next.t("Home:EmployeeMonthlyBenefits.Name"), textAlign: TextAlign.Left },
        {
            id: "employment",
            label: i18next.t("Home:EmployeeMonthlyBenefits.Employment"),
            textAlign: TextAlign.Left
        },
        { id: "value", label: i18next.t("Home:EmployeeMonthlyBenefits.Value"), textAlign: TextAlign.Right }
    ];

    const rows: IRow[] = [];

    try {
        const res = await customFetch(EMPLOYEE_MONTHLY_BENEFITS_URL);
        const data = await res.json() as IEmployeeMonthlyBenefitsData[];

        const hasMoreEmployments = data?.some(item => item.EmploymentName !== data[0].EmploymentName);
        if (!hasMoreEmployments) {
            columns = columns.filter(column => column.id !== "employment");
        }

        let id = 1;
        for (const item of data) {
            rows.push({
                // BE does not provide unique ID so create some fake one
                id: id ++,
                values: {
                    name: item.Name,
                    employment: item.EmploymentName,
                    value: CurrencyType.format(item.Value, {
                        currency: getCompanyCurrency(args.context),
                        minimumFractionDigits: 0
                    })
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
};

export function employeeMonthlyBenefitsTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:EmployeeMonthlyBenefits.Heading"),
        size: { w: 3, h: 2 },
        isVisible: isPayrollEnabledOnCompanyCallback,
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
        tableData: getEmployeeMonthlyBenefitsData
    };
}

export function getEmployeeBenefitLimitsTileDef(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        size: { w: 2, h: 3 },
        title: i18next.t("Home:EmployeeBenefitLimits.Heading"),
        isVisible: isPayrollEnabledOnCompanyCallback,
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
        component: BenefitLimitsProgress
    };
}

export interface ILeaveBalanceData {
    Name: string;
    EmploymentName: string;
    VacationBalanceMinutes: number;
    VacationBalanceDays: number;
}

const hasMoreEmployments = memoize(async (args: IGetValueArgs): Promise<boolean> => {
    if (!isPayrollEnabledOnCompany(args.context.getCompany())) {
        return false;
    }
    try {
        const data = await getEmployeeLeaveBalance(args.context);
        return !!data?.some(item => item.EmploymentName !== data[0].EmploymentName);
    } catch (e) {
        logger.error(e);
    }
    return false;
});

const hasSingleEmployment = async (args: IGetValueArgs): Promise<boolean> => {
    if (!isPayrollEnabledOnCompany(args.context.getCompany())) {
        return false;
    }
    return !(await hasMoreEmployments(args));
};

const getEmployeeLeaveBalanceData = async (args: IGetTileDataArgs): Promise<ITableTileData> => {
    const columns: IColumn[] = [
        { id: "name", label: i18next.t("Home:EmployeeLeaveBalance.Name"), textAlign: TextAlign.Left },
        { id: "employment", label: i18next.t("Home:EmployeeLeaveBalance.Employment"), textAlign: TextAlign.Left },
        { id: "remains", label: i18next.t("Home:EmployeeLeaveBalance.Remains"), textAlign: TextAlign.Right }
    ];

    const rows: IRow[] = [];

    try {
        const data = await getEmployeeLeaveBalance(args.context);

        for (const item of data) {
            const hasHours = !!item.VacationBalanceMinutes;
            const hours = roundToDecimalPlaces(1, item.VacationBalanceMinutes / 60);
            const daysLabel = getPluralizationLabel("Common:Time.Days", item.VacationBalanceDays);
            rows.push({
                id: item.Name + item.EmploymentName,
                values: {
                    name: item.Name,
                    employment: item.EmploymentName,
                    remains: `${hasHours ? `${hours} h | ` : ""}${roundToDecimalPlaces(2, item.VacationBalanceDays)} ${daysLabel}`
                }
            });
        }
    } catch (e) {
        logger.error(e);
    }

    return { rows, columns };
};

export function getEmployeeLeaveBalanceTileDef(): ITableTileInfo {
    return {
        type: DashboardTileType.Table,
        title: i18next.t("Home:EmployeeLeaveBalance.Heading"),
        size: { w: 3, h: 2 },
        tableData: getEmployeeLeaveBalanceData,
        isVisible: hasMoreEmployments, // async already includes isPayrollEnabledOnCompanyCallback check
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
    };
}

export function getEmployeeSingleLeaveBalanceTileDef(): ICustomTileInfo {
    return {
        type: DashboardTileType.Custom,
        title: i18next.t("Home:EmployeeLeaveBalance.Heading"),
        size: { w: 2, h: 2 },
        component: EmployeeLeaveBalanceTile,
        isVisible: hasSingleEmployment, // async already includes isPayrollEnabledOnCompanyCallback check
        CPs: [CustomerPermissionCodes.PrPayrollAgenda],
    };
}

